import React, { useState, useMemo } from "react";
import { useGetExpensesQuery, useCreateExpenseMutation, useUpdateExpenseMutation, useDeleteExpenseMutation } from "../services/api";
import { FiTrendingDown, FiPlus, FiEdit2, FiTrash2, FiDownload, FiCalendar, FiBarChart2 } from "react-icons/fi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB" }).format(value || 0);
}

export default function ExpensesPage() {
  const { data, isLoading } = useGetExpensesQuery();
  const [createExpense] = useCreateExpenseMutation();
  const [updateExpense] = useUpdateExpenseMutation();
  const [deleteExpense] = useDeleteExpenseMutation();

  const expenses = data?.data || [];
  
  // Date filter state
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showChart, setShowChart] = useState(true);

  // Filter expenses by date range
  const filteredExpenses = useMemo(() => {
    return expenses.filter(exp => {
      const expDate = new Date(exp.expense_date);
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;
      
      if (from && expDate < from) return false;
      if (to && expDate > to) return false;
      return true;
    });
  }, [expenses, dateFrom, dateTo]);

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + Number(e.amount), 0);
  const avgExpense = filteredExpenses.length > 0 ? totalExpenses / filteredExpenses.length : 0;

  // Chart data - expenses by category
  const chartDataByCategory = useMemo(() => {
    const categoryTotals = {};
    filteredExpenses.forEach(exp => {
      categoryTotals[exp.category] = (categoryTotals[exp.category] || 0) + Number(exp.amount);
    });
    return Object.entries(categoryTotals).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      amount: value
    }));
  }, [filteredExpenses]);

  // Chart colors
  const COLORS = ['#ffd700', '#0b3d2e', '#e05252', '#2ecc71', '#3498db', '#9b59b6', '#f39c12'];

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    category: "other",
    description: "",
    amount: "",
    payment_method: "cash",
    expense_date: new Date().toISOString().split("T")[0],
  });
  const [error, setError] = useState("");

  const handleExportCSV = () => {
    // Create CSV content
    const headers = ['Date', 'Category', 'Description', 'Amount (ETB)', 'Payment Method'];
    const rows = filteredExpenses.map(exp => [
      new Date(exp.expense_date).toLocaleDateString("en-ET"),
      exp.category,
      exp.description,
      exp.amount,
      exp.payment_method
    ]);

    let csvContent = headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    // Add summary
    csvContent += '\n';
    csvContent += `"Total Expenses","","","${totalExpenses}",""\n`;
    csvContent += `"Number of Transactions","","","${filteredExpenses.length}",""\n`;
    if (dateFrom || dateTo) {
      csvContent += `"Date Range","${dateFrom || 'All'}","to","${dateTo || 'All'}",""\n`;
    }

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `expenses_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleQuickFilter = (days) => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - days);
    setDateFrom(from.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
  };

  const openModal = (expense = null) => {
    if (expense) {
      setEditingId(expense.id);
      setForm({
        category: expense.category,
        description: expense.description,
        amount: expense.amount,
        payment_method: expense.payment_method,
        expense_date: expense.expense_date,
      });
    } else {
      setEditingId(null);
      setForm({
        category: "other",
        description: "",
        amount: "",
        payment_method: "cash",
        expense_date: new Date().toISOString().split("T")[0],
      });
    }
    setError("");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingId) {
        await updateExpense({ id: editingId, ...form }).unwrap();
      } else {
        await createExpense(form).unwrap();
      }
      setShowModal(false);
    } catch (err) {
      setError(err?.data?.message || "Failed to save expense");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this expense?")) {
      try {
        await deleteExpense(id).unwrap();
      } catch (err) {
        alert(err?.data?.message || "Failed to delete");
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ 
            height: "32px", 
            width: "180px", 
            background: "#E2E8F0", 
            borderRadius: "6px",
            marginBottom: "0.75rem",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
        </div>
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem"
        }}>
          {[1,2,3].map(i => (
            <div key={i} style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.5rem",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              <div style={{ height: "16px", width: "60%", background: "#E2E8F0", borderRadius: "4px", marginBottom: "1rem" }}></div>
              <div style={{ height: "32px", width: "40%", background: "#E2E8F0", borderRadius: "4px" }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
      {/* Page Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "2rem" 
      }}>
        <div>
          <h1 style={{ 
            fontSize: "2rem", 
            marginBottom: "0.5rem", 
            fontWeight: 600,
            color: "#1E293B"
          }}>
            Expenses
          </h1>
          <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
            Track and analyze business expenses
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button 
            onClick={handleExportCSV}
            style={{
              padding: "0.75rem 1.5rem",
              background: "white",
              color: "#3B82F6",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: 500,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#3B82F6";
              e.currentTarget.style.background = "#F8FAFC";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#E2E8F0";
              e.currentTarget.style.background = "white";
            }}
          >
            <FiDownload /> Export CSV
          </button>
          <button 
            onClick={() => openModal()}
            style={{
              padding: "0.75rem 1.5rem",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.3)";
            }}
          >
            <FiPlus /> Add Expense
          </button>
        </div>
      </div>

      {/* Date Filter & KPI Section */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        marginBottom: "2rem"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1.25rem" }}>
          <FiCalendar style={{ color: "#3B82F6" }} />
          <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", margin: 0 }}>
            Filter by Date
          </h3>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
          <div>
            <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 500, color: "#64748B", marginBottom: "0.5rem" }}>From Date</label>
            <input 
              type="date" 
              className="form-input" 
              value={dateFrom} 
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 500, color: "#64748B", marginBottom: "0.5rem" }}>To Date</label>
            <input 
              type="date" 
              className="form-input" 
              value={dateTo} 
              onChange={(e) => setDateTo(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "0.5rem" }}>
            <button 
              onClick={clearFilters}
              style={{
                flex: 1,
                padding: "0.65rem 1rem",
                background: "white",
                color: "#64748B",
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                fontSize: "0.9rem",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#64748B";
                e.currentTarget.style.background = "#F8FAFC";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#E2E8F0";
                e.currentTarget.style.background = "white";
              }}
            >
              Clear
            </button>
            <button 
              onClick={() => setShowChart(!showChart)}
              style={{
                flex: 1,
                padding: "0.65rem 1rem",
                background: "white",
                color: "#3B82F6",
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                fontSize: "0.9rem",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.35rem",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#3B82F6";
                e.currentTarget.style.background = "#F8FAFC";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#E2E8F0";
                e.currentTarget.style.background = "white";
              }}
            >
              <FiBarChart2 size={16} /> {showChart ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {/* Quick Filter Buttons */}
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button 
            onClick={() => handleQuickFilter(7)}
            style={{
              padding: "0.5rem 0.75rem",
              background: "#F8FAFC",
              color: "#64748B",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
              fontSize: "0.85rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#3B82F6";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.borderColor = "#3B82F6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#F8FAFC";
              e.currentTarget.style.color = "#64748B";
              e.currentTarget.style.borderColor = "#E2E8F0";
            }}
          >
            Last 7 Days
          </button>
          <button 
            onClick={() => handleQuickFilter(30)}
            style={{
              padding: "0.5rem 0.75rem",
              background: "#F8FAFC",
              color: "#64748B",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
              fontSize: "0.85rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#3B82F6";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.borderColor = "#3B82F6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#F8FAFC";
              e.currentTarget.style.color = "#64748B";
              e.currentTarget.style.borderColor = "#E2E8F0";
            }}
          >
            Last 30 Days
          </button>
          <button 
            onClick={() => handleQuickFilter(90)}
            style={{
              padding: "0.5rem 0.75rem",
              background: "#F8FAFC",
              color: "#64748B",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
              fontSize: "0.85rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#3B82F6";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.borderColor = "#3B82F6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#F8FAFC";
              e.currentTarget.style.color = "#64748B";
              e.currentTarget.style.borderColor = "#E2E8F0";
            }}
          >
            Last 90 Days
          </button>
          <button 
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
              setDateFrom(firstDay.toISOString().split('T')[0]);
              setDateTo(today.toISOString().split('T')[0]);
            }}
            style={{
              padding: "0.5rem 0.75rem",
              background: "#F8FAFC",
              color: "#64748B",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
              fontSize: "0.85rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#3B82F6";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.borderColor = "#3B82F6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#F8FAFC";
              e.currentTarget.style.color = "#64748B";
              e.currentTarget.style.borderColor = "#E2E8F0";
            }}
          >
            This Month
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Total Expenses */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💸
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Expenses
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(totalExpenses)}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#64748B", marginTop: "0.5rem" }}>
            {filteredExpenses.length} transactions
          </div>
        </div>

        {/* Average Expense */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              📊
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Average per Transaction
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(avgExpense)}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#64748B", marginTop: "0.5rem" }}>
            Mean expense amount
          </div>
        </div>

        {/* Date Range */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              📅
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Date Range
          </div>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1E293B" }}>
            {dateFrom || dateTo ? (
              <>{dateFrom || 'All'} to {dateTo || 'All'}</>
            ) : (
              'All Time'
            )}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#64748B", marginTop: "0.5rem" }}>
            Filtered period
          </div>
        </div>
      </div>

      {/* Charts */}
      {showChart && chartDataByCategory.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "1.25rem", marginBottom: "2rem" }}>
          {/* Bar Chart */}
          <div style={{
            background: "white",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            padding: "1.5rem"
          }}>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "1.25rem" }}>
              Expenses by Category
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartDataByCategory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" style={{ fontSize: "0.85rem" }} />
                <YAxis stroke="#64748B" style={{ fontSize: "0.85rem" }} />
                <Tooltip 
                  contentStyle={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Bar dataKey="amount" fill="#3B82F6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Pie Chart */}
          <div style={{
            background: "white",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            padding: "1.5rem"
          }}>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "1.25rem" }}>
              Category Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartDataByCategory}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="amount"
                >
                  {chartDataByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Expenses Table */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        overflowX: "auto"
      }}>
        <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "1.25rem" }}>
          Expense History
        </h3>
        <table style={{ 
          width: "100%", 
          borderCollapse: "collapse",
          fontSize: "0.9rem"
        }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #E2E8F0" }}>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Date</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Category</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Description</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Amount</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Payment</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredExpenses.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "3rem", color: "#64748B" }}>
                  <FiTrendingDown size={48} style={{ color: "#CBD5E1", marginBottom: "0.75rem" }} />
                  <div>{dateFrom || dateTo ? 'No expenses found for selected date range' : 'No expenses yet'}</div>
                </td>
              </tr>
            ) : (
              filteredExpenses.map((exp) => (
                <tr key={exp.id} style={{ 
                  borderBottom: "1px solid #F1F5F9",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                }}
                >
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{new Date(exp.expense_date).toLocaleDateString("en-ET")}</td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      background: "#FEF3C7",
                      color: "#92400E"
                    }}>
                      {exp.category}
                    </span>
                  </td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{exp.description}</td>
                  <td style={{ padding: "1rem 0.75rem", fontWeight: 700, color: "#EF4444" }}>{formatCurrency(exp.amount)}</td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{exp.payment_method}</td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button 
                        onClick={() => openModal(exp)}
                        style={{
                          padding: "0.5rem",
                          background: "transparent",
                          border: "1px solid #E2E8F0",
                          borderRadius: "6px",
                          color: "#3B82F6",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#F8FAFC";
                          e.currentTarget.style.borderColor = "#3B82F6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "#E2E8F0";
                        }}
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button 
                        onClick={() => handleDelete(exp.id)}
                        style={{
                          padding: "0.5rem",
                          background: "transparent",
                          border: "1px solid #E2E8F0",
                          borderRadius: "6px",
                          color: "#EF4444",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#FEF2F2";
                          e.currentTarget.style.borderColor = "#EF4444";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "#E2E8F0";
                        }}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Expense Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingId ? "Edit Expense" : "Add Expense"}</h3>
              <button className="btn-ghost" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="rent">Rent</option>
                    <option value="wifi">WiFi</option>
                    <option value="ekub">Ekub</option>
                    <option value="salary">Salary</option>
                    <option value="transport">Transport</option>
                    <option value="materials">Materials</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <input type="text" className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Amount (ETB) *</label>
                    <input type="number" className="form-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} step="0.01" min="0" required />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Date *</label>
                    <input type="date" className="form-input" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} required />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Method *</label>
                  <select className="form-select" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingId ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
